import { Controller, type Control, type UseFormSetValue } from "react-hook-form"

import { FilterBadgeList } from "@/components/search/FindSimilarButton"
import { LabeledMultiSelect, type LabeledOption } from "@/components/label-multiselect"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import type { FilterFormData } from "@/lib/search-filter-model"
import {
  CONTACTOUT_SENIORITY_LEVEL_OPTIONS,
  CONTACTOUT_YEARS_IN_CURRENT_ROLE_OPTIONS,
} from "@/types/contactOut"

interface JobFiltersSectionProps {
  control: Control<FilterFormData>
  formValues: FilterFormData
  isPending: boolean
  onAddJobTitle: () => void
  onFindSimilarJobTitles: () => Promise<void>
  onRemoveJobTitle: (title: string) => void
  setValue: UseFormSetValue<FilterFormData>
}

export function JobFiltersSection({
  control,
  formValues,
  isPending,
  onAddJobTitle,
  onFindSimilarJobTitles,
  onRemoveJobTitle,
  setValue,
}: JobFiltersSectionProps) {
  return (
    <div className="space-y-6">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label className="text-teal-600">Job Titles</Label>
          <div className="flex items-center gap-2">
            <Controller
              name="currentOnly"
              control={control}
              defaultValue="current-only"
              render={({ field }) => (
                <Select value={field.value ?? "current-only"} onValueChange={field.onChange}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current-only">Current Only</SelectItem>
                    <SelectItem value="current-past">Current + Past</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            <Button
              variant="link"
              size="sm"
              className="p-0 text-teal-600"
              onClick={() => setValue("jobTitles", [], { shouldDirty: true })}
              type="button"
            >
              Clear all
            </Button>
          </div>
        </div>
        <Controller
          name="jobTitleInput"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              placeholder="Start typing a job title and select from the list"
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault()
                  onAddJobTitle()
                }
              }}
            />
          )}
        />
        <FilterBadgeList
          items={formValues.jobTitles}
          onFindSimilar={onFindSimilarJobTitles}
          onRemove={onRemoveJobTitle}
          isPending={isPending}
          disabled={formValues.jobTitles.length === 0}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <LabeledMultiSelect
            onSelectedValuesChange={(values) => setValue("timeSpentMin", values, { shouldDirty: true })}
            selectedValues={formValues.timeSpentMin}
            placeholder="Tenure"
            options={CONTACTOUT_YEARS_IN_CURRENT_ROLE_OPTIONS as unknown as LabeledOption[]}
            label="Time at current role"
            bagdeTitle=""
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <LabeledMultiSelect
          onSelectedValuesChange={(values) => setValue("jobTitleLevels", values, { shouldDirty: true })}
          selectedValues={formValues.jobTitleLevels}
          placeholder="Select seniority levels"
          options={CONTACTOUT_SENIORITY_LEVEL_OPTIONS as unknown as LabeledOption[]}
          label="Job Title Levels"
          bagdeTitle="Title"
        />
      </div>
    </div>
  )
}
